import React, { useEffect, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import {
  useFetchDataDetail,
  useFetchDetail,
  usePostDetail,
} from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeroCst from "@components/HeroCst";
import AddEditFormCst from "@components/AddEditFormCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ColumnInterface } from "@components/Table/types";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { ColumnsType } from "src/interfaces/Common";
import { isTrueValue } from "@utils/utilsValidators";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useCustomersAddEdit } from "../hooks/useCustomersAddEdit";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";

const AddCustomer: React.FC = () => {
  const { t } = useTranslation(["customers-page"]);
  const { id } = useParams();
  const navigate = useNavigate();

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { columns, dataDetail, actionsForForms, mutate } = useFetchDetail(
    ServicesURL.customer,
    undefined,
    PagesID["corporate.customers"],
  );
  const { columns: columnsCustomFields, dataDetail: dataCustomFields } =
    useFetchDataDetail(
      `${ServicesURL.getCorporateCustomFields}?corporate_id=${id}`,
      PagesID["corporate.customFields"],
    );
  const { trigger: triggerCustomer } = usePostDetail(
    ServicesURL.postCustomer + `?corporate_id=${id}`,
  );

  const {
    openAssociations,
    industriesAssociation,
    professionsAssociation,
    jobsAssociation,
    inputsException,
    setOpenAssociation,
    handleAssociation,
    handleDelete,
    getValues,
  } = useCustomersAddEdit(dataDetail);

  const { trigger: fetchLanguages, data: corporateLanguages } = useSWRMutation(
    ServicesURL.corporateLanguages.replace(":corporateId", id || ""),
    (url: string) => fetcher(url, MethodType.GET),
  );

  useEffect(() => {
    if (id) fetchLanguages();
  }, [id]);

  const languageOptions = useMemo(() => {
    if (!corporateLanguages) return [];
    return [
      corporateLanguages.primary_language,
      ...(corporateLanguages.secondary_languages || []),
    ]
      .filter(Boolean)
      .map((l: any) => ({ value: l.id, label: l.label }));
  }, [corporateLanguages]);

  const columnsMerged = useMemo(() => {
    const mergedList: ColumnsType[] = [...columns];
    if (columnsCustomFields && dataCustomFields && dataCustomFields.catalog) {
      mergedList.push(
        ...columnsCustomFields
          .map((el) => {
            return {
              ...el,
              ...{
                header: dataCustomFields.catalog[el.accessorKey] || undefined,
              },
            };
          })
          .filter((el) => {
            return el.header !== undefined;
          }),
      );
    }

    return mergedList.map((col) =>
      col.accessorKey === "preference_language"
        ? { ...col, options: languageOptions }
        : col,
    );
  }, [columns, columnsCustomFields, dataCustomFields, languageOptions]);

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    // TODO: change roles format
    const objToSend: any = {
      name: inputForm["name"] || "",
      surname: inputForm["surname"] || "",
      email: inputForm["email"] || "", // must be mail format
      username: inputForm["username"] || "",
      phone_number: inputForm["phone_number"],
      address: inputForm["address"],
      gender: inputForm["gender"],
      fiscal_code: inputForm["fiscal_code"],
      country_code: inputForm["country_code"],
      country_description: inputForm["country_description"],
      birthday:
        inputForm["birthday"] && new Date(inputForm["birthday"]).getTime(),
      birth_country: inputForm["birth_country"],
      birth_province: inputForm["birth_province"],
      ateco_code: inputForm["ateco_code"] || null,
      branch: inputForm["branch"],
      external_employee_flag:
        inputForm["external_employee_flag"] &&
        isTrueValue(inputForm["external_employee_flag"]),
      contractual_framework_code: inputForm["contractual_framework_code"],
      contractual_framework_type: inputForm["contractual_framework_type"],
      new_hire_flag:
        inputForm["new_hire_flag"] && isTrueValue(inputForm["new_hire_flag"]),
      hire_date:
        inputForm["hire_date"] && new Date(inputForm["hire_date"]).getTime(),
      termination_date:
        inputForm["termination_date"] &&
        new Date(inputForm["termination_date"]).getTime(),
      contract_type: inputForm["contract_type"],
      schedule_type: inputForm["schedule_type"],
      ccnl: inputForm["ccnl"],
      language: inputForm["language"],
      supervisor: inputForm["supervisor"], // ?
      professional_level: inputForm["professional_level"],
      privacy_consent_flag:
        inputForm["privacy_consent_flag"] &&
        isTrueValue(inputForm["privacy_consent_flag"]),
      smart_working_flag:
        inputForm["smart_working_flag"] &&
        isTrueValue(inputForm["smart_working_flag"]),
      teacher_picture: inputForm["teacher_picture"],
      internal_employee_flag:
        inputForm["internal_employee_flag"] &&
        isTrueValue(inputForm["internal_employee_flag"]),
      roles: inputForm["roles"]?.map((el: any) => el.value) || [], // must be between 1 and 6
      smart_learning_hours: inputForm["smart_learning_hours"]
        ? parseInt(inputForm["smart_learning_hours"])
        : null,
      persona: inputForm["persona"]?.map((el: any) => el.value) || [],
      external_id: inputForm["external_id"] || "",
      admin_flag: false, // always false by requirements
      receieved_credentials_flag: isTrueValue(
        inputForm.receieved_credentials_flag,
      ),
      uog: inputForm["uog"],
      // do not send below properties
      // level1_proficiency: inputForm["level1_proficiency"] || [],
      // level2_proficiency: inputForm["level2_proficiency"] || [],
      // level3_proficiency: inputForm["level3_proficiency"] || [],
      // level4_proficiency: inputForm["level4_proficiency"] || [],
      job: jobsAssociation?.map((el: any) => el.id)[0] || null,
      industry: industriesAssociation?.map((el: any) => el.id)[0] || null,
      profession: professionsAssociation?.map((el: any) => el.id)[0] || null,
      // job_to_be: jobsTobeAssociation?.map((el: any) => +el.id)[0] || null,
      // industry_to_be:
      //   industriesTobeAssociation?.map((el: any) => +el.id)[0] || null,
      // profession_to_be:
      //   professionsTobeAssociation?.map((el: any) => +el.id)[0] || null,
      manager_flag:
        inputForm["manager_flag"] && isTrueValue(inputForm["manager_flag"]),
      c1value: inputForm["c1value"],
      c2value: inputForm["c2value"],
      c3value: inputForm["c3value"],
      c4value: inputForm["c4value"],
      c5value: inputForm["c5value"],
      c6value: inputForm["c6value"],
      c7value: inputForm["c7value"],
      c8value: inputForm["c8value"],
      c9value: inputForm["c9value"],
      c10value: inputForm["c10value"],
      c11value: inputForm["c11value"],
      c12value: inputForm["c12value"],
      c13value: inputForm["c13value"],
      c14value: inputForm["c14value"],
      c15value: inputForm["c15value"],
      c16value: inputForm["c16value"],
      c17value: inputForm["c17value"],
      c18value: inputForm["c18value"],
      c19value: inputForm["c19value"],
      c20value: inputForm["c20value"],
      preference_language: inputForm["preference_language"] || null,
    };
    try {
      await triggerCustomer(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
    mutate && mutate(inputForm);
  };

  const onDownload = (url: string) => null;

  return (
    <>
      {!columnsCustomFields || !corporateLanguages ? (
        <FormLoadingStateCst />
      ) : (
        <AnimationFadeIn>
          <BackgroundImageCst
            customWidth={"100%"}
            customHeight={"603px"}
            image="section-users.jpg"
            position={"absolute"}
            fullpage={true}
          />
          <Breadcrumb />
          <Container
            maxWidth={"xxl"}
            sx={{
              position: "relative",
              zIndex: 1,
            }}
          >
            <HeroCst
              title={t("add.hero-title")}
              description={t("add.hero-description")}
            />
            {columns && (
              <Box
                sx={{
                  mt: "25px",
                }}
              >
                <AddEditFormCst
                  isFaculties={true}
                  row={{
                    ...dataDetail,
                    preference_language:
                      corporateLanguages?.primary_language?.id.toString(),
                    // ...dataCustomFields?.catalog
                  }}
                  columns={columnsMerged || []}
                  onSubmit={onSubmit}
                  inputsException={inputsException}
                  actionsForForms={
                    actionsForForms &&
                    actionsForForms.map((el: ColumnInterface) => {
                      if (el.type?.includes("download")) {
                        el.downloadCallback = () =>
                          onDownload((el && el.service) || "");
                      }
                      return el;
                    })
                  }
                  formActionType={["download"]}
                  backButton={() => {
                    navigate(`/corporate/${id}/detail/users/`);
                  }}
                  setOpenAssociation={(accessorKey, open) =>
                    setOpenAssociation(accessorKey, open)
                  }
                  handleAssociation={(accessorKey, value) =>
                    handleAssociation(accessorKey, value)
                  }
                  handleDelete={handleDelete}
                  getValues={(accessorKey) => getValues(accessorKey)}
                  association={openAssociations}
                />
              </Box>
            )}
          </Container>
          <ModalConfirmsCst
            open={!!openToast}
            title={t("hero-title-add")}
            description={""}
            onCallBackClosed={() => {
              setOpenToast(undefined);
            }}
            status={openToast}
          />
        </AnimationFadeIn>
      )}
    </>
  );
};

export default AddCustomer;
